/**
  Channel Leads API wrapper

  Handles API calls for channel leads management (affiliate partners like rates.now)
**/

import { api } from "../common/utils"
import {
  CHANNELS_ENDPOINT,
  CHANNEL_BALANCE_ENDPOINT,
  CHANNEL_TRANSACTIONS_ENDPOINT,
  CHANNEL_STATS_ENDPOINT,
  CHANNEL_BALANCE_RECONCILE_ENDPOINT,
  CHANNEL_LEADS_UNPAID_ENDPOINT,
  CHANNEL_LEADS_EXPORT_ENDPOINT,
  CHANNEL_LEADS_RETRY_ENDPOINT,
  CHANNEL_LEADS_RETRY_ORG_ENDPOINT,
  CHANNEL_LEAD_REFUND_ENDPOINT,
  CHANNEL_LEADS_FORGIVE_ENDPOINT,
  CHANNEL_AUTO_RELOAD_ENDPOINT
} from "../common/constants"
import {
  FindChannelsParams,
  FindChannelsResponse,
  CreateChannelResponse,
  DeleteChannelResponse,
  GetBalanceResponse,
  AddFundsResponse,
  GetTransactionsParams,
  GetTransactionsResponse,
  GetStatsParams,
  GetStatsResponse,
  SystemChannelStats,
  ChannelBalanceReconcile,
  GetUnpaidLeadsResponse,
  RetryBillingResponse,
  RetryBillingOrgResponse,
  RefundChannelLeadPayload,
  RefundChannelLeadResponse,
  ForgiveLeadsArgs,
  ForgiveLeadsResponse,
  Channel,
  GetAutoReloadSettingsResponse,
  UpdateAutoReloadSettingsResponse
} from "../types"

const channelLeads = {
  // Channels
  findChannels: async (
    params: FindChannelsParams
  ): Promise<FindChannelsResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value))
      }
    })
    const { data } = await api.get(
      `${CHANNELS_ENDPOINT}?${queryParams.toString()}`
    )
    return data
  },

  createOrUpdateChannel: async (
    payload: Partial<Channel> & { org_id: string }
  ): Promise<CreateChannelResponse> => {
    const { data } = await api.post(CHANNELS_ENDPOINT, payload)
    return data
  },

  deleteChannel: async (id: string): Promise<DeleteChannelResponse> => {
    const { data } = await api.delete(`${CHANNELS_ENDPOINT}?id=${id}`)
    return data
  },

  // Balance
  getBalance: async (org_id: string): Promise<GetBalanceResponse> => {
    const { data } = await api.get(
      `${CHANNEL_BALANCE_ENDPOINT}?org_id=${org_id}`
    )
    return data
  },

  addFunds: async (payload: {
    org_id: string
    amount_cents: number
    description?: string
  }): Promise<AddFundsResponse> => {
    const { data } = await api.post(CHANNEL_BALANCE_ENDPOINT, payload)
    return data
  },

  // Transactions
  getTransactions: async (
    params: GetTransactionsParams
  ): Promise<GetTransactionsResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value))
      }
    })
    const { data } = await api.get(
      `${CHANNEL_TRANSACTIONS_ENDPOINT}?${queryParams.toString()}`
    )
    return data
  },

  // Stats
  getStats: async (params: GetStatsParams): Promise<GetStatsResponse> => {
    const queryParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, String(value))
      }
    })
    const { data } = await api.get(
      `${CHANNEL_STATS_ENDPOINT}?${queryParams.toString()}`
    )
    return data
  },

  // System-wide stats breakdown (administrator only) — all=1, no org_id
  getSystemStats: async (params: {
    start?: number
    stop?: number
  }): Promise<SystemChannelStats> => {
    const queryParams = new URLSearchParams({ all: "1" })
    if (params.start) queryParams.append("start", String(params.start))
    if (params.stop) queryParams.append("stop", String(params.stop))
    const { data } = await api.get(
      `${CHANNEL_STATS_ENDPOINT}?${queryParams.toString()}`
    )
    return data
  },

  // Reconcile one org's channel ledger against its Stripe balance
  // (administrator only). Dry run: reads Stripe, writes nothing.
  getBalanceReconcile: async (
    org_id: number
  ): Promise<ChannelBalanceReconcile> => {
    const { data } = await api.get(
      `${CHANNEL_BALANCE_RECONCILE_ENDPOINT}?org_id=${org_id}`
    )
    return data
  },

  // Write the Stripe balance transactions missing from the ledger; `adopt`
  // lists reviewed Stripe adjustment ids (cbtxn_*) to write as well
  applyBalanceReconcile: async (payload: {
    org_id: number
    adopt?: string[]
  }): Promise<ChannelBalanceReconcile> => {
    const { data } = await api.post(CHANNEL_BALANCE_RECONCILE_ENDPOINT, payload)
    return data
  },

  // CSV of the period's charged channel leads (all channels, or channel_id)
  exportLeadsCsv: async (params: {
    org_id: string
    start: number
    stop: number
    channel_id?: string
  }): Promise<string> => {
    const { data } = await api.get(CHANNEL_LEADS_EXPORT_ENDPOINT, {
      params,
      responseType: "text"
    })
    return data
  },

  // Unpaid Leads
  getUnpaidLeads: async (org_id: string): Promise<GetUnpaidLeadsResponse> => {
    const { data } = await api.get(
      `${CHANNEL_LEADS_UNPAID_ENDPOINT}?org_id=${org_id}`
    )
    return data
  },

  // System-wide unpaid backlog (administrator only) — all=1, no org_id
  getSystemUnpaidLeads: async (): Promise<GetUnpaidLeadsResponse> => {
    const { data } = await api.get(`${CHANNEL_LEADS_UNPAID_ENDPOINT}?all=1`)
    return data
  },

  // postpaid (super admin only) charges leads the balance cannot cover on
  // the org's account instead of leaving them unpaid
  retryBilling: async (
    lead_ids: string[],
    postpaid = false
  ): Promise<RetryBillingResponse> => {
    const { data } = await api.post(CHANNEL_LEADS_RETRY_ENDPOINT, {
      lead_ids,
      postpaid
    })
    return data
  },

  // Retries billing for the org's whole unpaid backlog, oldest first
  retryBillingOrg: async (
    org_id: string,
    postpaid = false
  ): Promise<RetryBillingOrgResponse> => {
    const { data } = await api.post(CHANNEL_LEADS_RETRY_ORG_ENDPOINT, {
      org_id: Number(org_id),
      postpaid
    })
    return data
  },

  // Refund a billed (disputed) lead — administrator only
  refundLead: async (
    payload: RefundChannelLeadPayload
  ): Promise<RefundChannelLeadResponse> => {
    const { data } = await api.post(CHANNEL_LEAD_REFUND_ENDPOINT, payload)
    return data
  },

  // Forgive unpaid leads — administrator only, never billed afterwards
  forgiveLeads: async (
    payload: ForgiveLeadsArgs
  ): Promise<ForgiveLeadsResponse> => {
    const { data } = await api.post(CHANNEL_LEADS_FORGIVE_ENDPOINT, payload)
    return data
  },

  // Auto-Reload (org-wide)
  getAutoReloadSettings: async (
    org_id: string
  ): Promise<GetAutoReloadSettingsResponse> => {
    const { data } = await api.get(
      `${CHANNEL_AUTO_RELOAD_ENDPOINT}?org_id=${org_id}`
    )
    return data
  },

  updateAutoReloadSettings: async (payload: {
    org_id: string
    auto_reload_enabled?: boolean
    min_balance_cents?: number
    reload_amount_cents?: number
    postpaid_enabled?: boolean
    postpaid_monthly_cap_cents?: number
  }): Promise<UpdateAutoReloadSettingsResponse> => {
    const { data } = await api.post(CHANNEL_AUTO_RELOAD_ENDPOINT, payload)
    return data
  }
}

export default channelLeads
